import React, { useEffect, useState } from 'react';
import { getGenerationStats, getTemplateUsageStats } from '../../services/adminService';

interface GenerationStat {
  date: string;
  totalGenerations: number;
  uniqueUsers: number;
}

interface TemplateUsageItem {
  template: string;
  count: number;
}

const Statistics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ stats: GenerationStat[] } | null>(null);
  const [templates, setTemplates] = useState<{ templateUsage: TemplateUsageItem[] } | null>(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, t] = await Promise.all([
        getGenerationStats(days),
        getTemplateUsageStats(days)
      ]);
      setStats(s);
      setTemplates(t);
    } catch (err) {
      console.error('Failed to load statistics', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const maxGenerations = stats?.stats
    ? Math.max(...stats.stats.map(s => s.totalGenerations), 1)
    : 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Statistics</h1>
        <p className="text-sm text-gray-600">Generation and usage analytics</p>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="space-x-2">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded ${days === d ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}
            >
              Last {d} days
            </button>
          ))}
        </div>
        <button onClick={load} className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-700">Refresh</button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-8">
          {/* Generations over time */}
          <div className="bg-white rounded shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Generations Over Time</h2>
            </div>
            <div className="p-4">
              {!stats || stats.stats.length === 0 ? (
                <p className="text-sm text-gray-500">No data</p>
              ) : (
                <div className="space-y-2">
                  {stats.stats.map(item => (
                    <div key={item.date} className="flex items-center">
                      <div className="w-40 text-sm text-gray-600">{item.date}</div>
                      <div className="flex-1 h-3 bg-gray-100 rounded">
                        <div
                          className="h-3 bg-indigo-500 rounded"
                          style={{ width: `${(item.totalGenerations / maxGenerations) * 100}%` }}
                        />
                      </div>
                      <div className="w-24 text-right text-sm text-gray-700 ml-3">{item.totalGenerations}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Template usage */}
          <div className="bg-white rounded shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Template Usage</h2>
            </div>
            <div className="p-4">
              {!templates || templates.templateUsage.length === 0 ? (
                <p className="text-sm text-gray-500">No data</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {templates.templateUsage.slice(0, 10).map(t => (
                    <div key={t.template} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                      <div className="text-sm text-gray-700 truncate pr-4">{t.template}</div>
                      <div className="text-sm font-medium">{t.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
